import type { CompositionEntry } from '@internal-audit/domain';

import { CompositionPanel } from '../shared/CompositionPanel';

import { formatCompositionEntry } from './compositionUtils';
import * as styles from './styles';
import type { CompositionBarChartProps } from './types';

/**
 * CompositionBarChart component - Extracted reusable bar chart logic
 * Handles rendering of composition data with consistent styling and colors
 * Now includes overflow handling with fade-out gradient and tooltip
 */
export function CompositionBarChart({ entries, totalItems, colorType, title, subtitle }: CompositionBarChartProps) {
  const hasData = entries && entries.length > 0;
  const countText = hasData ? `${entries.length} types` : 'No data';

  // Split entries into visible and hidden for overflow handling
  const MAX_VISIBLE_ITEMS = 5;
  const visibleEntries = hasData ? entries.slice(0, MAX_VISIBLE_ITEMS) : [];
  const hiddenEntries = hasData ? entries.slice(MAX_VISIBLE_ITEMS) : [];

  return (
    <div className={styles.detailsPanel}>
      <div className={styles.detailsPanelHeader}>
        <span className={styles.detailsPanelTitle}>{title}</span>
        <span className={styles.compositionCount}>{countText}</span>
      </div>

      {hasData ? (
        <>
          {subtitle && <div className={styles.compositionSubheading}>{subtitle}</div>}
          <CompositionPanel colorType={colorType} hiddenItems={hiddenEntries} title={title} totalItems={totalItems} visibleItems={visibleEntries} />
        </>
      ) : (
        <div className={styles.detailsPanelContent}>
          <div className={styles.noCompositionMessage}>No {title.toLowerCase()} composition data available</div>
        </div>
      )}
    </div>
  );
}

/**
 * CompositionBarSection component - Handles multiple sections with subheadings
 * Used for Language & Topics panel that has multiple subsections
 */
export function CompositionBarSection({ entries, totalItems, colorType, title }: Omit<CompositionBarChartProps, 'subtitle'>) {
  const hasData = entries && entries.length > 0;
  const countText = hasData ? `${entries.length} types` : 'No data';

  return (
    <div className={styles.detailsPanel}>
      <div className={styles.detailsPanelHeader}>
        <span className={styles.detailsPanelTitle}>{title}</span>
        <span className={styles.compositionCount}>{countText}</span>
      </div>

      <div className={styles.detailsPanelContent}>
        {hasData ? (
          entries.map((entry: CompositionEntry, index: number) => {
            const formattedEntry = formatCompositionEntry(entry, {
              totalItems,
              colorType,
              index,
            });

            return (
              <div key={entry.key} className={styles.compositionBarItem}>
                <div className={styles.compositionBarLabel}>
                  <div
                    className={styles.compositionBarColor}
                    style={
                      {
                        '--bar-color': formattedEntry.color,
                      } as React.CSSProperties & { '--bar-color': string }
                    }
                  />
                  <span className={styles.compositionBarText}>{formattedEntry.key}</span>
                </div>
                <div className={styles.compositionBarTrack}>
                  <div
                    className={styles.compositionBarFill}
                    style={
                      {
                        '--bar-width': `${formattedEntry.percentage}%`,
                        '--bar-color': formattedEntry.color,
                      } as React.CSSProperties & {
                        '--bar-width': string;
                        '--bar-color': string;
                      }
                    }
                  />
                </div>
                <span className={styles.compositionBarValue}>{formattedEntry.displayText}</span>
              </div>
            );
          })
        ) : (
          <div className={styles.noCompositionMessage}>No {title.toLowerCase()} composition data available</div>
        )}
      </div>
    </div>
  );
}
